/* eslint-disable indent */
import { Duration } from 'ts-luxon';

import {
	NoiseReductionLevel,
	DenoiserLevel,
	DeverbIntensity,
	OutputSignalPath
} from '@shure/shared/angular/data-access/system-api/core';
import {
	mapAnalogLevelFromSysApi,
	mapDeviceEqualizerContourFromSysApi,
	mapInputSourceFromSysApi,
	mapNonLinearProcessingLevelFromSysApi,
	mapSpatialSeparationFromSysApi,
	mapSpeakerTypeToChannelLoudspeaker
} from '@shure/shared/angular/data-access/system-api/mappers';
import { ChannelOutputPath, EqFilterType } from '@shure/shared/angular/data-access/system-api/models';

import { DspCopyChannel } from '../../api/models';
import { ContextMenuCopyChannelFragment } from '../graphql/generated/copy-channel.generated';

export function mapDurationToMillisFromSysApi(duration: string | null | undefined, defaultValue = 0): number {
	return duration ? Duration.fromISO(duration).toMillis() : defaultValue;
}

export function mapDurationMilliesToISO(duration: number | null | undefined, defaultValue = 0): string {
	return <string>Duration.fromMillis(duration || defaultValue).toISO();
}

export function mapAcousticEchoCancellationMonoRef(
	aecFragment: ContextMenuCopyChannelFragment['features']['acousticEchoCancellation'],
	aecMonoRefFragment: ContextMenuCopyChannelFragment['features']['acousticEchoCancellationMonoReference']
): DspCopyChannel['features']['acousticEchoCancellation'] {
	return {
		enabled: aecFragment?.enabled || false,
		selectedMonoReferenceSourceId: aecMonoRefFragment?.selectedReferenceSource.id || '',
		nonLinearProcessingLevel: aecFragment?.nonLinearProcessingLevel
			? mapNonLinearProcessingLevelFromSysApi(aecFragment?.nonLinearProcessingLevel)
			: undefined
	};
}

export function mapAcousticEchoCancellationStereoRef(
	aecFragment: ContextMenuCopyChannelFragment['features']['acousticEchoCancellation'],
	aecStereoRefFragment: ContextMenuCopyChannelFragment['features']['acousticEchoCancellationStereoReference']
): DspCopyChannel['features']['acousticEchoCancellation'] {
	return {
		enabled: aecFragment?.enabled || false,
		stereoReference: {
			selectedLeftReferenceSourceId: aecStereoRefFragment?.selectedLeftReferenceSource.id || '',
			selectedRightReferenceSourceId: aecStereoRefFragment?.selectedRightReferenceSource.id || ''
		},
		nonLinearProcessingLevel: aecFragment?.nonLinearProcessingLevel
			? mapNonLinearProcessingLevelFromSysApi(aecFragment?.nonLinearProcessingLevel)
			: undefined
	};
}

function mapAutomaticGainControl(
	fragment: ContextMenuCopyChannelFragment['features']['automaticGainControl']
): DspCopyChannel['features']['automaticGainControl'] {
	return {
		enabled: fragment?.enabled || false,
		maxBoost: fragment?.maxBoost || 0,
		maxCut: fragment?.maxCut || 0,
		targetLevel: fragment?.targetLevel || 0
	};
}

function mapCompressor(
	fragment: ContextMenuCopyChannelFragment['features']['compressor']
): DspCopyChannel['features']['compressor'] {
	return {
		enabled: fragment?.enabled || false,
		ratio: fragment?.ratio || 0,
		threshold: fragment?.threshold || 0
	};
}

function mapDelay(fragment: ContextMenuCopyChannelFragment['features']['delay']): DspCopyChannel['features']['delay'] {
	return {
		enabled: fragment?.enabled || false,
		delay: mapDurationToMillisFromSysApi(fragment?.delay)
	};
}

function mapLimiter(
	fragment: ContextMenuCopyChannelFragment['features']['limiter']
): DspCopyChannel['features']['limiter'] {
	return {
		enabled: fragment?.enabled || false,
		bypassed: fragment?.bypassed || false,
		threshold: fragment?.threshold || 0
	};
}

function mapNoiseReduction(
	fragment: ContextMenuCopyChannelFragment['features']['noiseReduction']
): DspCopyChannel['features']['noiseReduction'] {
	return {
		enabled: fragment?.enabled || false,
		level: fragment?.level || NoiseReductionLevel.Low
	};
}

function mapDenoiser(
	fragment: ContextMenuCopyChannelFragment['features']['denoiser']
): DspCopyChannel['features']['denoiser'] {
	return {
		enabled: fragment?.enabled || false,
		level: fragment?.level || DenoiserLevel.Low
	};
}

function mapDeverb(
	fragment: ContextMenuCopyChannelFragment['features']['deverb']
): DspCopyChannel['features']['deverb'] {
	return {
		enabled: fragment?.enabled || false,
		intensity: fragment?.intensity || DeverbIntensity.Low
	};
}

function mapEqualizer(
	fragment: ContextMenuCopyChannelFragment['features']['equalizer']
): DspCopyChannel['features']['equalizer'] {
	const filters = fragment?.filters || [];
	return {
		isPeq: filters.some(
			(f) => f.description.constraints.configuration?.filterType?.options?.includes(EqFilterType.Parametric)
		),
		filters: filters.map((filter) => ({
			id: filter.id,
			constraints: {
				gainStep: filter.description.constraints.gain?.gain.range.step ?? 0.1
			},
			features: {
				configuration: {
					enabled: filter.features.configuration?.enabled || false,
					filterType: filter.features.configuration?.filterType || EqFilterType.Parametric
				},
				frequency: filter.features.frequency?.frequency || 20,
				gain: filter.features.gain?.gain || 0,
				qFactor: filter.features.qFactor?.qFactor || 0
			}
		}))
	};
}

function mapDeviceSpecificEqualizerContour(
	fragment: ContextMenuCopyChannelFragment['features']['deviceSpecificEqualizerContour']
): DspCopyChannel['features']['deviceSpecificEqualizerContour'] {
	if (fragment) {
		const deviceSpecificEqualizerContour = mapDeviceEqualizerContourFromSysApi(
			fragment.deviceSpecificEqualizerContour
		);
		return { deviceSpecificEqualizerContour };
	}
	return null;
}

function mapInputSource(
	fragment: ContextMenuCopyChannelFragment['features']['inputSource']
): DspCopyChannel['features']['inputSource'] {
	if (fragment) {
		const source = mapInputSourceFromSysApi(fragment.source);
		return { source };
	}
	return null;
}

function mapOutputPath(
	fragment: ContextMenuCopyChannelFragment['features']['outputPath']
): DspCopyChannel['features']['outputPath'] {
	if (fragment) {
		const path =
			fragment.path === OutputSignalPath.PostDigitalSignalProcessing
				? ChannelOutputPath.PostDsp
				: ChannelOutputPath.PreDsp;
		return { path };
	}
	return null;
}

export function mapContextMenuCopyChannelFromSysApi(fragment: ContextMenuCopyChannelFragment): DspCopyChannel {
	return {
		id: fragment.id,
		features: {
			autoMute: fragment.features.autoMute?.enabled || null,
			gain: fragment.features.gain?.gain || 0,
			muted: fragment.features.mute?.muted || false,
			analogLevel: fragment.features.analogLevel?.level
				? mapAnalogLevelFromSysApi(fragment.features.analogLevel?.level)
				: undefined,
			acousticEchoCancellation: fragment.features.acousticEchoCancellationMonoReference
				? mapAcousticEchoCancellationMonoRef(
						fragment.features.acousticEchoCancellation,
						fragment.features.acousticEchoCancellationMonoReference
				  )
				: mapAcousticEchoCancellationStereoRef(
						fragment.features.acousticEchoCancellation,
						fragment.features.acousticEchoCancellationStereoReference
				  ),
			automaticGainControl: mapAutomaticGainControl(fragment.features.automaticGainControl),
			compressor: mapCompressor(fragment.features.compressor),
			delay: mapDelay(fragment.features.delay),
			equalizer: mapEqualizer(fragment.features.equalizer),
			deviceSpecificEqualizerContour: mapDeviceSpecificEqualizerContour(
				fragment.features.deviceSpecificEqualizerContour
			),
			limiter: mapLimiter(fragment.features.limiter),
			noiseReduction: mapNoiseReduction(fragment.features.noiseReduction),
			denoiser: mapDenoiser(fragment.features.denoiser),
			deverb: mapDeverb(fragment.features.deverb),
			inputSource: mapInputSource(fragment.features.inputSource),
			outputPath: mapOutputPath(fragment.features.outputPath),
			passiveSpeakers: {
				type: mapSpeakerTypeToChannelLoudspeaker(fragment.features.passiveSpeakers?.type)
			},
			pcPeripheral: fragment.features.pcPeripheral
				? {
						selectedPeripheralId: fragment.features.pcPeripheral.selectedPeripheral?.id || ''
				  }
				: null,
			channelBonding: fragment.features.channelBonding?.spatialSeparation
				? {
						spatialSeparation: mapSpatialSeparationFromSysApi(
							fragment.features.channelBonding.spatialSeparation
						)
				  }
				: null
		}
	};
}
